import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMetricsController } from '../../features/metrics/state/useMetricsController'
import { ErrorState } from './States'
import { AnalyticsSkeleton } from './AnalyticsSkeleton'
import { cn } from '@/lib/utils'
import { normalizePlatform, SOCIAL_PLATFORMS } from '../../features/posts/model/platforms'

export const AnalyticsOverview = memo(function AnalyticsOverview() {
  const { t } = useTranslation()
  const { metrics, isLoading, error } = useMetricsController()

  if (isLoading) return <AnalyticsSkeleton />
  if (error) return <ErrorState error={error} title={t('analytics.title') + ' Error'} />
  if (!metrics) return null

  const totalPlatformsCount = metrics.platforms?.reduce((acc, p) => acc + p.count, 0) ?? 0

  return (
    <div className="flex flex-col gap-6 p-6">
      <h2 className="m-0 text-lg font-semibold">{t('analytics.title')}</h2>

      {metrics.volume && (
        <AnalyticsCard
          title={t('analytics.volume')}
          value={metrics.volume.total.toString()}
          change={metrics?.volume?.change}
          trend={metrics?.volume?.change?.startsWith('+') ? 'up' : 'down'}
          description={t('analytics.volumeDesc')}
        />
      )}

      {metrics.engagement && (
        <AnalyticsCard
          title={t('analytics.engagement')}
          value={metrics.engagement.total.toString()}
          change={metrics.engagement.change}
          trend={metrics.engagement.change.startsWith('+') ? 'up' : 'down'}
          description={t('analytics.engagementDesc')}
        />
      )}

      {metrics.sentiment && (
        <AnalyticsCard
          title={t('analytics.sentiment')}
          value={metrics.sentiment.positivePercentage}
          change={metrics.sentiment.change}
          trend={metrics.sentiment.change.startsWith('+') ? 'up' : 'down'}
          description={t('analytics.sentimentDesc')}
        />
      )}

      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="mb-4 text-sm font-semibold text-muted-foreground">
          {t('analytics.platformBreakdown')}
        </h3>
        <div className="flex flex-col gap-4">
          {metrics.platforms?.map((p) => {
            const percentage = totalPlatformsCount
              ? Math.round((p.count / totalPlatformsCount) * 100)
              : 0

            const normalized = normalizePlatform(p.name)
            const color =
              normalized === SOCIAL_PLATFORMS.FACEBOOK
                ? '#1877f2'
                : normalized === SOCIAL_PLATFORMS.X
                  ? '#1da1f2'
                  : 'var(--text-3)'

            return (
              <PlatformBar
                key={p.name}
                label={t(`platforms.${normalized}`)}
                value={percentage}
                color={color}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
})

type AnalyticsCardProps = {
  title: string
  value: string
  change: string
  trend: 'up' | 'down'
  description: string
}

const AnalyticsCard = memo(function AnalyticsCard({
  title,
  value,
  change,
  trend,
  description,
}: AnalyticsCardProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 text-xs font-medium text-muted-foreground">{title}</div>
      <div className="mb-1 text-2xl font-bold text-foreground">{value}</div>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'text-[13px] font-medium',
            trend === 'up' ? 'text-trend-up' : 'text-trend-down',
          )}
        >
          {change}
        </span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
    </div>
  )
})

type PlatformBarProps = {
  label: string
  value: number
  color: string
}

const PlatformBar = memo(function PlatformBar({ label, value, color }: PlatformBarProps) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[13px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold text-foreground">{value}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded bg-secondary">
        <div
          className="h-full transition-[width] duration-300 ease-out"
          style={{ width: `${value}%`, background: color }}
        />
      </div>
    </div>
  )
})
